import React, { useState, useEffect, useRef } from 'react';
import { Camera, CheckCircle, XCircle, Loader2, Store } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Html5Qrcode } from 'html5-qrcode';
import { storeQrService } from '../services/dynamicQr';
import { storesService } from '../services/stores';
import { toast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';

const ScanQR = () => {
  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scannedToken, setScannedToken] = useState(null);
  const [benefits, setBenefits] = useState([]);
  const [selectedBenefitId, setSelectedBenefitId] = useState(null);
  const [scannerName, setScannerName] = useState('');
  const [ticketNumber, setTicketNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    loadStores();
  }, []);

  useEffect(() => {
    return () => {
      // Limpiar scanner al desmontar
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  }, []);

  const loadStores = async () => {
    try {
      const response = await storesService.getAll();
      if (response.success && response.data) {
        setStores(response.data);
      }
    } catch (err) {
      console.error('Error cargando tiendas:', err);
      toast({
        title: "Error",
        description: "No se pudieron cargar las tiendas",
        variant: "destructive",
      });
    }
  };

  const checkCameraPermissions = async () => {
    try {
      // Verificar si la API de MediaDevices está disponible
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Tu navegador no soporta acceso a la cámara. Por favor, usa Chrome, Firefox, Safari o Edge.');
      }

      // Verificar si estamos en HTTPS o localhost
      const isSecureContext = window.isSecureContext || 
        window.location.protocol === 'https:' || 
        window.location.hostname === 'localhost' || 
        window.location.hostname === '127.0.0.1';

      if (!isSecureContext) {
        throw new Error('Se requiere HTTPS para acceder a la cámara. Por favor, accede a través de una conexión segura.');
      }

      // Intentar obtener permisos de cámara
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        // Si llegamos aquí, tenemos permisos - detener el stream inmediatamente
        stream.getTracks().forEach(track => track.stop());
        return true;
      } catch (permissionError) {
        if (permissionError.name === 'NotAllowedError' || permissionError.name === 'PermissionDeniedError') {
          throw new Error('Permisos de cámara denegados. Por favor, permite el acceso a la cámara en la configuración de tu navegador.');
        } else if (permissionError.name === 'NotFoundError' || permissionError.name === 'DevicesNotFoundError') {
          throw new Error('No se encontró ninguna cámara. Por favor, conecta una cámara e intenta nuevamente.');
        } else if (permissionError.name === 'NotReadableError' || permissionError.name === 'TrackStartError') {
          throw new Error('La cámara está siendo usada por otra aplicación. Por favor, cierra otras aplicaciones que usen la cámara.');
        } else {
          throw new Error(`Error al acceder a la cámara: ${permissionError.message}`);
        }
      }
    } catch (err) {
      throw err;
    }
  };

  const startScanning = async () => {
    if (!selectedStoreId) {
      toast({
        title: "Selecciona una tienda",
        description: "Por favor, selecciona una tienda antes de escanear",
        variant: "destructive",
      });
      return;
    }

    try {
      setScanning(true);
      setError(null);
      setScannedToken(null);
      setBenefits([]);
      setSelectedBenefitId(null);
      setSuccess(false);

      // Verificar permisos antes de iniciar
      await checkCameraPermissions();

      const html5QrCode = new Html5Qrcode("qr-reader");
      html5QrCodeRef.current = html5QrCode;

      // Intentar primero con cámara trasera (environment)
      let cameraConfig = { facingMode: "environment" };
      let qrboxSize = { width: 250, height: 250 };

      try {
        await html5QrCode.start(
          cameraConfig,
          {
            fps: 10,
            qrbox: qrboxSize,
            aspectRatio: 1.0,
          },
          (decodedText) => {
            handleScannedCode(decodedText);
          },
          (errorMessage) => {
            // Ignorar errores de escaneo continuo (no son críticos)
            // Solo loguear si es un error importante
            if (errorMessage && !errorMessage.includes('NotFoundException')) {
              console.debug('QR scan error:', errorMessage);
            }
          }
        );
      } catch (startError) {
        // Si falla con cámara trasera, intentar con cámara frontal
        console.warn('Error con cámara trasera, intentando con cámara frontal:', startError);
        
        try {
          await html5QrCode.stop();
        } catch (stopError) {
          // Ignorar error al detener
        }

        // Intentar con cámara frontal
        cameraConfig = { facingMode: "user" };
        await html5QrCode.start(
          cameraConfig,
          {
            fps: 10,
            qrbox: qrboxSize,
            aspectRatio: 1.0,
          },
          (decodedText) => {
            handleScannedCode(decodedText);
          },
          (errorMessage) => {
            if (errorMessage && !errorMessage.includes('NotFoundException')) {
              console.debug('QR scan error:', errorMessage);
            }
          }
        );
      }
    } catch (err) {
      console.error('Error iniciando escáner:', err);
      setScanning(false);
      
      // Mensaje de error más específico
      let errorMessage = "No se pudo acceder a la cámara.";
      
      if (err.message) {
        errorMessage = err.message;
      } else if (err.name === 'NotAllowedError') {
        errorMessage = "Permisos de cámara denegados. Por favor, permite el acceso a la cámara en la configuración de tu navegador.";
      } else if (err.name === 'NotFoundError') {
        errorMessage = "No se encontró ninguna cámara. Por favor, conecta una cámara e intenta nuevamente.";
      } else if (err.name === 'NotReadableError') {
        errorMessage = "La cámara está siendo usada por otra aplicación. Por favor, cierra otras aplicaciones que usen la cámara.";
      }

      setError(errorMessage);
      toast({
        title: "Error de cámara",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const stopScanning = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      } catch (err) {
        console.error('Error deteniendo escáner:', err);
      }
      html5QrCodeRef.current = null;
    }
    setScanning(false);
  };

  const handleScannedCode = async (token) => {
    // Detener el escáner inmediatamente
    await stopScanning();

    setScannedToken(token);
    setLoading(true);
    setError(null);
    setBenefits([]);
    setSelectedBenefitId(null);

    try {
      const response = await storeQrService.validate(token, parseInt(selectedStoreId));

      if (response.success && response.status === 'valid') {
        // Recibir lista de beneficios disponibles
        setBenefits(response.data.benefits || []);
        
        if (response.data.benefits && response.data.benefits.length === 0) {
          setError('No hay beneficios disponibles para esta tienda');
        }
      } else {
        setError(response.message || 'Código QR inválido');
        toast({
          title: "QR Inválido",
          description: response.message || 'Código QR inválido',
          variant: "destructive",
        });
      }
    } catch (err) {
      console.error('Error validando QR:', err);
      setError(err.message || 'Error al validar el código QR');
      toast({
        title: "Error",
        description: err.message || 'Error al validar el código QR',
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async () => {
    if (!selectedBenefitId) {
      toast({
        title: "Selecciona un beneficio",
        description: "Por favor, selecciona un beneficio antes de aplicar",
        variant: "destructive",
      });
      return;
    }

    if (!ticketNumber.trim()) {
      toast({
        title: "Número de ticket requerido",
        description: "Por favor, ingresa el número de ticket",
        variant: "destructive",
      });
      return;
    }

    if (!scannerName.trim()) {
      toast({
        title: "Nombre requerido",
        description: "Por favor, ingresa el nombre de quien escanea",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await storeQrService.redeem(
        scannedToken,
        parseInt(selectedStoreId),
        selectedBenefitId,
        ticketNumber.trim(),
        scannerName.trim()
      );

      if (response.success) {
        setSuccess(true);
        toast({
          title: "¡Beneficio aplicado!",
          description: "El beneficio se ha aplicado correctamente",
          variant: "default",
        });

        // Limpiar estado después de 3 segundos
        setTimeout(() => {
          resetState();
        }, 3000);
      } else {
        throw new Error(response.error || 'Error al aplicar el beneficio');
      }
    } catch (err) {
      console.error('Error redimiendo QR:', err);
      setError(err.message || 'Error al aplicar el beneficio');
      toast({
        title: "Error",
        description: err.message || 'Error al aplicar el beneficio',
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetState = () => {
    setScannedToken(null);
    setBenefits([]);
    setSelectedBenefitId(null);
    setScannerName('');
    setTicketNumber('');
    setError(null);
    setSuccess(false);
    setLoading(false);
  };

  const handleNewScan = () => {
    resetState();
    startScanning();
  };

  return (
    <div className="min-h-screen bg-black px-6 py-8">
      <Toaster />
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-2">Escanear QR</h1>
          <p className="text-[#A2A2A2]">
            Escanea el código QR del cliente para aplicar el beneficio
          </p>
        </div>

        {/* Selector de tienda */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <Label htmlFor="store" className="text-white mb-2 block">
            <Store className="w-4 h-4 inline mr-2" />
            Selecciona la tienda
          </Label>
          <select
            id="store"
            value={selectedStoreId}
            onChange={(e) => setSelectedStoreId(e.target.value)}
            disabled={scanning || loading}
            className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-white/50 disabled:opacity-50"
          >
            <option value="">-- Selecciona una tienda --</option>
            {stores.map((store) => (
              <option key={store.id} value={store.id}>
                {store.name}
              </option>
            ))}
          </select>
        </div>

        {/* Campo nombre de quien escanea */}
        {!scannedToken && !benefits.length && !success && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <Label htmlFor="scanner-name" className="text-white mb-2 block">
              Nombre de quien escanea *
            </Label>
            <Input
              id="scanner-name"
              type="text"
              value={scannerName}
              onChange={(e) => setScannerName(e.target.value)}
              placeholder="Ingresa tu nombre"
              className="bg-white/10 border-white/20 text-white"
              disabled={scanning || loading}
            />
          </div>
        )}

        {/* Área de escaneo */}
        {!scannedToken && !benefits.length && !success && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4">
            {!scanning ? (
              <div className="space-y-4">
                <div className="aspect-square bg-black/20 rounded-xl flex items-center justify-center">
                  <Camera className="w-16 h-16 text-white/50" />
                </div>
                {error && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                )}
                <Button
                  onClick={startScanning}
                  disabled={!selectedStoreId || !scannerName.trim() || loading}
                  className="w-full"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Iniciar escaneo
                </Button>
                {!window.isSecureContext && window.location.protocol !== 'https:' && 
                 window.location.hostname !== 'localhost' && 
                 window.location.hostname !== '127.0.0.1' && (
                  <p className="text-yellow-400 text-xs text-center">
                    ⚠️ Nota: Se requiere HTTPS para acceder a la cámara en producción
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div id="qr-reader" className="aspect-square rounded-xl overflow-hidden bg-black"></div>
                <Button
                  onClick={stopScanning}
                  variant="outline"
                  className="w-full"
                >
                  Detener escaneo
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Loading */}
        {loading && !benefits.length && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
            <Loader2 className="w-8 h-8 text-white animate-spin mx-auto mb-4" />
            <p className="text-white">Validando código QR...</p>
          </div>
        )}

        {/* Error */}
        {error && !benefits.length && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-6">
            <div className="flex items-center gap-3 text-red-400">
              <XCircle className="w-5 h-5" />
              <p className="text-white">{error}</p>
            </div>
            <Button
              onClick={handleNewScan}
              variant="outline"
              className="w-full mt-4"
            >
              Intentar nuevamente
            </Button>
          </div>
        )}

        {/* Lista de beneficios disponibles */}
        {benefits.length > 0 && !success && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4">
            <div className="flex items-center gap-3 text-green-400 mb-4">
              <CheckCircle className="w-5 h-5" />
              <h3 className="text-xl font-bold text-white">Código QR válido</h3>
            </div>

            <div className="space-y-3">
              <Label className="text-white mb-2 block">Selecciona un beneficio *</Label>
              {benefits.map((benefit) => (
                <div
                  key={benefit.id}
                  onClick={() => setSelectedBenefitId(benefit.id)}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    selectedBenefitId === benefit.id
                      ? 'border-white bg-white/10'
                      : 'border-white/10 bg-white/5 hover:border-white/30'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                      selectedBenefitId === benefit.id
                        ? 'border-white bg-white'
                        : 'border-white/30'
                    }`}>
                      {selectedBenefitId === benefit.id && (
                        <div className="w-2 h-2 rounded-full bg-black" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium">{benefit.title}</p>
                      <p className="text-[#A2A2A2] text-sm mt-1">{benefit.description}</p>
                      {benefit.store_name && (
                        <p className="text-[#A2A2A2] text-xs mt-1">Tienda: {benefit.store_name}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {selectedBenefitId && (
              <>
                <div className="pt-4 border-t border-white/10 space-y-4">
                  <div>
                    <Label htmlFor="ticket" className="text-white mb-2 block">
                      Número de ticket *
                    </Label>
                    <Input
                      id="ticket"
                      type="text"
                      value={ticketNumber}
                      onChange={(e) => setTicketNumber(e.target.value)}
                      placeholder="Ingresa el número de ticket"
                      className="bg-white/10 border-white/20 text-white"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={handleRedeem}
                    disabled={!ticketNumber.trim() || !scannerName.trim() || loading}
                    className="flex-1"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Aplicando...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Aplicar beneficio
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={resetState}
                    variant="outline"
                    disabled={loading}
                  >
                    Cancelar
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Éxito */}
        {success && (
          <div className="bg-green-500/10 border border-green-500/20 rounded-2xl p-8 text-center space-y-4">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto" />
            <h3 className="text-2xl font-bold text-white">¡Beneficio aplicado!</h3>
            <p className="text-[#A2A2A2]">
              El beneficio se ha aplicado correctamente al ticket {ticketNumber}
            </p>
            <Button
              onClick={handleNewScan}
              className="w-full mt-4"
            >
              Escanear otro QR
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScanQR;

